<!-- eslint-disable vue/multi-word-component-names -->
<template>
	<div :class="'flex h-screen w-full flex-col'">
		<Anouncement
			v-if="announcements.length && isAuthenticated()"
			:key="announcements.length"
			class="h-[50px]"
			:announcements="announcements"
			@closed="handleCloseAnnouncement"
		/>

		<PaymentRecoveryBanner
			v-if="isAuthenticated()"
		/>

		<BeachCommuteWai
			v-if="isBeachComuteUser()"
		/>

		<AppHeader />

		<div
			v-if="loaded"
			:class="[
				isBeachComuteUser() && announcements.length > 0 ?
					'h-[calc(100vh-178px)]': isBeachComuteUser() ?
						'h-[calc(100vh-128px)]' : announcements.length > 0 ?
							'h-[calc(100vh-110px)]' : 'h-[calc(100vh-60px)]',
				'flex',
			]"
		>

			<sidebar
				@new-resource="handleNewResource"
			/>

			<div class="flex w-full min-w-0 flex-col">
				<div
					class="relative flex size-full min-h-0 grow overflow-auto"
				>
					<div class="min-w-0 grow">
						<slot />
					</div>
				</div>
			</div>
		</div>
    
		<AppointmentTypeform
			v-if="typeformAppointmentVisible && isAuthenticated()"
			@close="handleCloseTypeformAppointment"
			@submitted="handleTypeformSubmitted"
		/>
    
		<WebinarModal
			:show-modal="showWebinarModal && isAuthenticated()"
			:close-modal="hideWebinarModal"
		/>
    
		<ChangeTemplateModal
			v-if="!isCoverLetter && loaded"
			:templates="templates"
			:visible="selectTemplateVisible"
			@close="() => selectTemplateVisible=false"
			@change="handleChangeTemplate"
		/>

		<NudgePopup />

		<ExitIntentModal v-if="isAuthenticated()" />
		<FirstApplicationPaywallModal v-if="isAuthenticated() && showPaywallModal" />
		<FirstApplicationCoachingModal v-if="isAuthenticated() && showCoachingModal" />
	</div>
</template>

<script setup>
import { storeToRefs } from 'pinia';


import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { hashInput, getStorageJson } from '../utils/helpers';
import { setAuthState } from '../composables/auth';
import { useKeyboardShortcut } from '../composables/useKeyboardShortcut';
import { useBuilder } from '../store/builder.store';
import { useFeatureUsageStore } from '../store/feature-usage.store';
import { useSidebarStore } from '../store/sidebar.store';
import { useOnboardingChecklist } from '../store/onboarding-checklist.store';
import { useOnboardingRetentionStore } from '../store/onboarding-retention.store';

import { getAnnouncements } from '../api/announcements';
import { user as fetchCurrentUser } from '../api/auth';
import { getAll as getAllTemplates } from '../api/templates';
// TODO: REVERT - Remove this import when Typeform post-onboarding experiment is reverted
import { update as updateProfile } from '../api/profile';
import { SALES_GATED_LOCATIONS } from '../utils/locations';

// import components
import Sidebar from '../components/sidebar.vue';
import AppHeader from '../components/app-header.vue';
 
import Anouncement from '../components/anouncement';
import PaymentRecoveryBanner from '../components/payment-recovery-banner.vue';
import BeachCommuteWai from '../components/beach-commute-wai.vue';

import AppointmentTypeform from '../modals/appointment-calendly.vue';
import ChangeTemplateModal from '../modals/change-template.vue';
import WebinarModal from '../modals/webinar-modal.vue';

import NudgePopup from '../components/activities/nudge-popup.vue';
import ExitIntentModal from '../modals/onboarding/exit-intent-modal.vue';
import FirstApplicationPaywallModal from '../modals/onboarding/first-application-paywall-modal.vue';
import FirstApplicationCoachingModal from '../modals/onboarding/first-application-coaching-modal.vue';

const router = useRouter();
const route = useRoute();

const isCoverLetter = ref(false);
const showWebinarModal = ref(false);

watch(
	() => route.meta?.coverLetter,
	async (newIsCoverLetter) => {
		isCoverLetter.value = newIsCoverLetter ?? false;
	}, { immediate: true }
);

const { isAuthenticated, getUser, isBeachComuteUser, setUser, setUserData } = setAuthState();

const builder = useBuilder();
const featuresUsageStore = useFeatureUsageStore();
const sidebarStore = useSidebarStore();
const onboardingChecklistStore = useOnboardingChecklist();
const onboardingRetentionStore = useOnboardingRetentionStore();
const { showPaywallModal, showCoachingModal } = storeToRefs(onboardingRetentionStore);

useKeyboardShortcut('[', () => sidebarStore.sidebarCollapseHandler());

const typeformAppointmentVisible = ref(false);

// TODO: REVERT - Remove updateUserOptions when Typeform post-onboarding experiment is reverted
const updateUserOptions = (opts) => updateProfile({ options: opts }).then((r) => setUser(r.data.data));

// TODO: REVERT - Remove this listener when Typeform post-onboarding experiment is reverted
// Listen for Typeform trigger from App.vue after premium nudge closes
const showTypeformAppointment = () => {
	if (!typeformAppointmentVisible.value) {
		typeformAppointmentVisible.value = true;
	}
};

onMounted(() => {
	onboardingChecklistStore.handleCloseOnboardingChecklist();

	// TODO: REVERT - Remove event listener when Typeform post-onboarding experiment is reverted
	window.addEventListener('show-typeform-appointment', showTypeformAppointment);

	const user = getUser();
	const userEmail = user?.email ?? '';
	const userOptions = user?.options;

	if (localStorage.getItem(`showWebinarPopup${hashInput(userEmail)}`) === 'true') {
		showWebinarModal.value = true;
	}

	if (localStorage.getItem(`newTypeformAppointment${hashInput(userEmail)}`) === 'true') {
		typeformAppointmentVisible.value = true;
	}

	// TODO: REVERT - Remove this block when Typeform post-onboarding experiment is reverted
	// Show Typeform once for existing users on dashboard who are in allowed locations and haven't seen it
	if (
		isAuthenticated() &&
		route.name === 'home' &&
		!user?.current_plan &&
		!userOptions?.typeform_shown &&
		!userOptions?.show_premium_nudge &&
		SALES_GATED_LOCATIONS.includes(userOptions?.location)
	) {
		typeformAppointmentVisible.value = true;
		updateUserOptions({ typeform_shown: true });
	}

	if (!userOptions?.onboarding_checklist_passed && userOptions?.home_passed) {
		onboardingChecklistStore.handleShowOnboardingChecklist();
	}
});

onUnmounted(() => {
	window.removeEventListener('show-typeform-appointment', showTypeformAppointment);
});

const hideWebinarModal = () => {
	showWebinarModal.value = false;
	localStorage.removeItem(`showWebinarPopup${hashInput(getUser()?.email ?? '')}`);
};

const handleCloseTypeformAppointment = () => {
	typeformAppointmentVisible.value = false;
	localStorage.removeItem(`newTypeformAppointment${hashInput(getUser()?.email ?? '')}`);

	// TODO: REVERT - Remove this block when Typeform post-onboarding experiment is reverted
	// Track that user dismissed the Typeform modal
	updateUserOptions({ typeform_shown: true, typeform_dismissed: true });
};

const handleTypeformSubmitted = () => {
	typeformAppointmentVisible.value = false;
	localStorage.removeItem(`newTypeformAppointment${hashInput(getUser()?.email ?? '')}`);

	// TODO: REVERT - Remove this block when Typeform post-onboarding experiment is reverted
	updateUserOptions({ typeform_shown: true, typeform_submitted: true });
};

const announcements = ref([]);
const closedAnnouncements = getStorageJson('closedAnnouncements');

const handleCloseAnnouncement = (uuid) => {
	announcements.value = announcements.value.filter((el) => uuid !== el.uuid);
};

const loaded = ref(false);
const selectTemplateVisible = ref(false);

const handleNewResource = async() => {
	// allow to change templates only for resumes
	if (! isCoverLetter.value) {
		if (templates.value.length) {
			selectTemplateVisible.value = true;
			return;
		}
	}

	try {
		await builder.init(null, router.replace, route);
	} catch (err) {
		if (err?.response?.status === 429) {
			featuresUsageStore.setShowLimitExceed('cover_letter');
		}
	}
};

const templates = ref([]);

const handleChangeTemplate = ({ template }) => {
	localStorage.setItem('defaultTemplate', template);
	builder.init(null, router.replace, route);
};

onMounted(async() => {
	if (isAuthenticated())  {
		try {
			const [announcementsResults, templatesResults] = await Promise.all([getAnnouncements(), getAllTemplates()]);
			announcements.value = announcementsResults.filter((el) => !closedAnnouncements.includes(el.uuid));
			templates.value = templatesResults;
		// eslint-disable-next-line no-unused-vars
		} catch (error) {
			//ignore
		} finally {
			loaded.value = true;
		}
	} else {
		loaded.value = true;
	}

	// Listen for application metrics updates
	if (isAuthenticated()) {
		window.Echo.private(`users.${  getUser().id}`)
			.listen('UpdateUserMetricEvent', async () => {
				const { data } = await fetchCurrentUser();
				setUserData(data);
			});
	}
});
</script>
